import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePhoneCheck, EXISTING_PATIENT_MSG } from "@/hooks/usePhoneCheck";
import ExistingPatientAlert from "@/components/ExistingPatientAlert";

const STATUSES = ["scheduled", "arrived", "treatment", "finished", "cancel"];
const BOOKING_SOURCES = [
  ["whatsapp", "WhatsApp"], ["instagram", "Instagram"], ["tiktok", "TikTok"],
  ["ads", "Ads (Meta/Google)"], ["walk_in", "Walk-in"], ["referral", "Referral"], ["other", "Lainnya"],
];
const ATTENDANCE = [
  ["pending", "Belum ditandai"], ["show_up", "Closing / Show Up"],
  ["no_show_reschedule", "Not Show Up - Reschedule"], ["no_show_cancel", "Not Show Up - Cancel"],
];

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function BookingFormDialog({ open, onOpenChange, booking, onSaved, branches }) {
  const { user } = useAuth();
  const isCsCrm = ["cs", "crm"].includes(user?.role);
  const isFo = user?.role === "fo_cs";
  const isEdit = Boolean(booking?.id);
  const [form, setForm] = useState({
    patient_id: "",
    patient_label: "",
    doctor_id: "",
    therapist_id: "",
    treatment_id: "",
    room_id: "",
    branch_id: "",
    booking_at: "",
    duration_min: 60,
    cs_tag: "",
    customer_category: "",
    booking_source: "",
    attendance_status: "pending",
    consultation_type: "consultation",
    request_doctor: "",
    request_room: "",
    notes: "",
    status: "scheduled",
  });
  const [saving, setSaving] = useState(false);

  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [patientOpen, setPatientOpen] = useState(false);
  const [treatmentSearch, setTreatmentSearch] = useState("");
  const [treatPickerOpen, setTreatPickerOpen] = useState(false);

  // Initial defaults
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        patient_id: booking.patient_id || "",
        patient_label: booking.patient_name || "",
        doctor_id: booking.doctor_id || "",
        therapist_id: booking.therapist_id || "",
        treatment_id: booking.treatment_id || "",
        room_id: booking.room_id || "",
        branch_id: booking.branch_id || "",
        booking_at: toLocalInputValue(booking.booking_at),
        duration_min: booking.duration_min || 60,
        cs_tag: booking.cs_tag || "",
        customer_category: booking.customer_category || "",
        booking_source: booking.booking_source || "",
        attendance_status: booking.attendance_status || "pending",
        consultation_type: booking.consultation_type || "consultation",
        request_doctor: booking.request_doctor || "",
        request_room: booking.request_room || "",
        notes: booking.notes || "",
        status: booking.status || "scheduled",
      });
    } else {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
      setForm((f) => ({
        ...f,
        patient_id: "",
        patient_label: "",
        branch_id: branches?.[0]?.id || "",
        booking_at: toLocalInputValue(now.toISOString()),
        status: "scheduled",
      }));
    }
  }, [open, booking, branches, isEdit]);

  // Load lookups by branch
  useEffect(() => {
    if (!open || !form.branch_id) return;
    Promise.all([
      api.get("/doctors", { params: { branch_id: form.branch_id } }),
      api.get("/therapists", { params: { branch_id: form.branch_id } }),
      api.get("/treatments"),
      api.get("/rooms", { params: { branch_id: form.branch_id } }),
    ]).then(([d, th, tr, rm]) => {
      setDoctors(d.data);
      setTherapists(th.data);
      setTreatments(tr.data);
      setRooms(rm.data);
    });
  }, [open, form.branch_id]);

  // Patient search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api
        .get("/patients", { params: { search: patientSearch, limit: 30 } })
        .then((r) => setPatients(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [patientSearch, open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSelectTreatment = (tid) => {
    if (tid === "__consult__") {
      setForm((f) => ({ ...f, treatment_id: "__consult__", consultation_type: "consultation" }));
      setTreatPickerOpen(false);
      return;
    }
    const t = treatments.find((x) => x.id === tid);
    setForm((f) => ({ ...f, treatment_id: tid, duration_min: t?.duration_min || f.duration_min }));
    setTreatPickerOpen(false);
  };

  const selectedTreatmentLabel =
    form.treatment_id === "__consult__"
      ? "Konsultasi Dokter - Belum Menentukan Treatment"
      : (treatments.find((x) => x.id === form.treatment_id)?.name || "");

  const filteredTreatments = treatments.filter((t) =>
    (t.name || "").toLowerCase().includes(treatmentSearch.toLowerCase())
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    const treatmentOut = (form.treatment_id && form.treatment_id !== "__consult__") ? form.treatment_id : null;
    if (!form.patient_id || !form.branch_id || !form.booking_at) {
      toast.error("Pasien, cabang, dan jadwal wajib diisi");
      return;
    }
    if (!isCsCrm && !treatmentOut) {
      toast.error("Treatment wajib dipilih");
      return;
    }
    if (isCsCrm) {
      if (!form.customer_category) { toast.error("Kategori Customer wajib diisi"); return; }
      if (!form.booking_source) { toast.error("Sumber Booking wajib diisi"); return; }
    }
    setSaving(true);
    try {
      const bookingAtIso = new Date(form.booking_at).toISOString();
      const payload = {
        patient_id: form.patient_id,
        doctor_id: form.doctor_id || null,
        therapist_id: form.therapist_id || null,
        treatment_id: treatmentOut,
        room_id: form.room_id || null,
        branch_id: form.branch_id,
        booking_at: bookingAtIso,
        duration_min: Number(form.duration_min) || 60,
        cs_tag: form.cs_tag || null,
        customer_category: form.customer_category || null,
        booking_source: form.booking_source || null,
        consultation_type: (!treatmentOut ? "consultation" : (form.consultation_type || "consultation")),
        request_doctor: form.request_doctor || null,
        request_room: form.request_room || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        const editPayload = { ...payload };
        if (isCsCrm) delete editPayload.status; else editPayload.status = form.status;
        await api.patch(`/bookings/${booking.id}`, editPayload);
        toast.success("Booking diperbarui");
      } else {
        await api.post("/bookings", payload);
        toast.success("Booking baru ditambahkan");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  // ---- Quick-add new patient (IMP 5) ----
  const PATIENT_SOURCES = [
    ["whatsapp", "WhatsApp"], ["instagram", "Instagram"], ["tiktok", "TikTok"],
    ["ads", "Ads"],
    ...(isFo ? [["ads_ots", "Ads OTS"], ["ads_cs_cabang", "Ads CS Cabang"]] : []),
    ["walk_in", "Walk-in"], ["referral", "Referral"], ["other", "Lainnya"],
  ];
  const [npOpen, setNpOpen] = useState(false);
  const [npSaving, setNpSaving] = useState(false);
  const emptyNp = () => ({ name: "", phone: "", birthday: "", gender: "F", branch_id: form.branch_id || branches?.[0]?.id || "", source: "whatsapp" });
  const [np, setNp] = useState(emptyNp());
  const openNewPatient = () => { setNp(emptyNp()); setNpOpen(true); };
  const { existing: npExisting } = usePhoneCheck(np.phone, npOpen);
  useEffect(() => {
    if (!npExisting) return;
    setNp((cur) => ({
      ...cur, name: npExisting.name || cur.name, birthday: npExisting.birthday || "",
      gender: npExisting.gender || cur.gender, source: npExisting.source || cur.source,
    }));
  }, [npExisting]);
  const pickExistingPatient = (p) => {
    setForm((f) => ({ ...f, patient_id: p.id, patient_label: p.name, branch_id: f.branch_id || p.branch_id, ...(isFo && p.source ? { booking_source: p.source } : {}) }));
    toast.info(EXISTING_PATIENT_MSG);
    setNpOpen(false);
  };
  const saveNewPatient = async () => {
    if (npExisting) { pickExistingPatient(npExisting); return; }
    if (!np.name.trim() || !np.phone.trim()) { toast.error("Nama & No. WhatsApp wajib diisi"); return; }
    if (!np.branch_id) { toast.error("Pilih cabang"); return; }
    setNpSaving(true);
    try {
      const { data } = await api.post("/patients", {
        name: np.name.trim(), phone: np.phone.trim(),
        birthday: np.birthday || null, gender: np.gender, branch_id: np.branch_id,
        source: np.source, no_ktp: true,
      });
      if (data.already_exists) { pickExistingPatient(data); return; }
      setForm((f) => ({ ...f, patient_id: data.id, patient_label: data.name, branch_id: np.branch_id, ...(isFo ? { booking_source: np.source } : {}) }));
      toast.success(`Pasien ${data.name} tersimpan & terpilih`);
      setNpOpen(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal menyimpan pasien");
    } finally { setNpSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Edit Booking" : "Tambah Booking Baru"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Reschedule, ganti dokter, atau perbarui detail." : "Buat slot booking baru untuk pasien."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Patient picker */}
          <div className="md:col-span-2">
            <Label>Pasien *</Label>
            <Popover open={patientOpen} onOpenChange={setPatientOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="booking-patient-trigger"
                  className="w-full justify-start font-normal"
                >
                  {form.patient_label || "Pilih pasien..."}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[480px]" align="start">
                <div className="border-b border-border p-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="booking-patient-search"
                      placeholder="Cari nama / no HP..."
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {patients.length === 0 && (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      Tidak ada pasien.
                    </div>
                  )}
                  {patients.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`pick-patient-${p.id}`}
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          patient_id: p.id,
                          patient_label: p.name,
                          branch_id: p.branch_id || f.branch_id,
                        }));
                        setPatientOpen(false);
                      }}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
                    >
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.phone}</div>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="booking-new-patient-btn"
              onClick={openNewPatient}
              className="mt-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Pasien Baru
            </Button>
          </div>

          <div>
            <Label>Cabang *</Label>
            <Select value={form.branch_id} onValueChange={(v) => setField("branch_id", v)}>
              <SelectTrigger data-testid="booking-branch-select">
                <SelectValue placeholder="Pilih cabang" />
              </SelectTrigger>
              <SelectContent>
                {branches?.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Tanggal & Jam *</Label>
            <Input
              type="datetime-local"
              data-testid="booking-datetime-input"
              value={form.booking_at}
              onChange={(e) => setField("booking_at", e.target.value)}
              required
            />
          </div>

          <div>
            <Label>Treatment {isCsCrm ? <span className="text-xs text-muted-foreground font-normal">(opsional)</span> : "*"}</Label>
            <button
              type="button"
              data-testid="booking-treatment-select"
              onClick={() => { setTreatmentSearch(""); setTreatPickerOpen(true); }}
              className="mt-1 flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <span className={selectedTreatmentLabel ? "" : "text-muted-foreground"}>
                {selectedTreatmentLabel || "Pilih treatment"}
              </span>
              <Search className="h-4 w-4 opacity-60 shrink-0" />
            </button>
          </div>

          <div>
            <Label>Durasi (menit)</Label>
            <Input
              type="number"
              min="15"
              step="15"
              data-testid="booking-duration-input"
              value={form.duration_min}
              onChange={(e) => setField("duration_min", e.target.value)}
            />
          </div>

          <div>
            <Label>Tipe Kunjungan (Tagging Konsultasi) *</Label>
            <Select value={form.consultation_type} onValueChange={(v) => setField("consultation_type", v)}>
              <SelectTrigger data-testid="booking-consultation-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consultation">Konsultasi Dokter</SelectItem>
                <SelectItem value="no_consultation">Tanpa Konsultasi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Request Dokter</Label>
            <Select
              value={form.doctor_id || "none"}
              onValueChange={(v) => setField("doctor_id", v === "none" ? "" : v)}
            >
              <SelectTrigger data-testid="booking-doctor-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Tidak ditentukan —</SelectItem>
                {doctors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Therapist</Label>
            <Select
              value={form.therapist_id || "none"}
              onValueChange={(v) => setField("therapist_id", v === "none" ? "" : v)}
            >
              <SelectTrigger data-testid="booking-therapist-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Tidak ditentukan —</SelectItem>
                {therapists.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Room Treatment</Label>
            <Select
              value={form.room_id || "none"}
              onValueChange={(v) => setField("room_id", v === "none" ? "" : v)}
            >
              <SelectTrigger data-testid="booking-room-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Tidak ditentukan —</SelectItem>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Tagging CS / CRM</Label>
            <Input
              data-testid="booking-cs-input"
              value={form.cs_tag}
              onChange={(e) => setField("cs_tag", e.target.value)}
              placeholder="cth. CS Maya"
            />
          </div>

          {isCsCrm && (
            <>
              <div>
                <Label>Kategori Customer *</Label>
                <Select value={form.customer_category} onValueChange={(v) => setField("customer_category", v)}>
                  <SelectTrigger data-testid="booking-customer-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_customer">New Customer</SelectItem>
                    <SelectItem value="repeat_customer">Repeat Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sumber Booking *</Label>
                <Select value={form.booking_source} onValueChange={(v) => setField("booking_source", v)}>
                  <SelectTrigger data-testid="booking-source-select"><SelectValue placeholder="Pilih sumber" /></SelectTrigger>
                  <SelectContent>
                    {BOOKING_SOURCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {isEdit && (
            <div>
              <Label>Kehadiran (Closing / Show Up)</Label>
              <Select
                value={form.attendance_status}
                onValueChange={async (v) => {
                  setField("attendance_status", v);
                  try {
                    await api.patch(`/bookings/${booking.id}/attendance`, { attendance_status: v });
                    toast.success("Status kehadiran diperbarui");
                    onSaved?.();
                  } catch (err) {
                    toast.error(formatApiError(err?.response?.data?.detail) || "Gagal update kehadiran");
                  }
                }}
              >
                <SelectTrigger data-testid="booking-attendance-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ATTENDANCE.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {isEdit && (
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="booking-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="md:col-span-2">
            <Label>Catatan</Label>
            <Textarea
              data-testid="booking-notes-input"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              rows={2}
            />
          </div>

          <DialogFooter className="md:col-span-2 mt-2">
            <Button
              type="button"
              variant="outline"
              data-testid="booking-cancel-btn"
              onClick={() => onOpenChange(false)}
            >
              Batal
            </Button>
            <Button type="submit" data-testid="booking-save-btn" disabled={saving}>
              {saving ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : "Tambah Booking"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Mobile-friendly Treatment picker: input at top, large tappable rows, keyboard never covers results */}
      <Dialog open={treatPickerOpen} onOpenChange={setTreatPickerOpen}>
        <DialogContent
          data-testid="treatment-picker-dialog"
          className="max-w-md p-0 gap-0 flex flex-col max-h-[80vh] !top-4 !translate-y-0"
        >
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="text-base">Pilih Treatment</DialogTitle>
          </DialogHeader>
          <div className="px-4 pb-3 border-b">
            <Input
              autoFocus
              data-testid="booking-treatment-search"
              inputMode="search"
              placeholder="Cari treatment..."
              value={treatmentSearch}
              onChange={(e) => setTreatmentSearch(e.target.value)}
              className="h-12 text-base"
            />
          </div>
          <div className="overflow-y-auto py-1" style={{ WebkitOverflowScrolling: "touch" }}>
            {isCsCrm && (
              <button
                type="button"
                data-testid="booking-treatment-consult"
                onClick={() => onSelectTreatment("__consult__")}
                className="w-full px-4 py-3 text-left text-base hover:bg-emerald-50 active:bg-emerald-100 border-b border-border/50"
              >
                Konsultasi Dokter - Belum Menentukan Treatment
              </button>
            )}
            {filteredTreatments.map((t) => (
              <button
                type="button"
                key={t.id}
                data-testid={`treatment-opt-${t.id}`}
                onClick={() => onSelectTreatment(t.id)}
                className={`w-full px-4 py-3 text-left text-base hover:bg-emerald-50 active:bg-emerald-100 ${t.id === form.treatment_id ? "bg-emerald-50 font-medium text-emerald-800" : ""}`}
              >
                {t.name}
              </button>
            ))}
            {filteredTreatments.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">Tidak ada treatment cocok</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick-add new patient mini form (IMP 5) */}
      <Dialog open={npOpen} onOpenChange={setNpOpen}>
        <DialogContent data-testid="new-patient-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Pasien Baru</DialogTitle>
            <DialogDescription>Simpan pasien baru langsung tanpa keluar dari form booking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nama *</Label>
              <Input data-testid="np-name" className="mt-1 h-11" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} placeholder="Nama pasien" />
            </div>
            <div>
              <Label>Nomor WhatsApp *</Label>
              <Input data-testid="np-phone" className="mt-1 h-11" inputMode="tel" value={np.phone} onChange={(e) => setNp({ ...np, phone: e.target.value })} placeholder="08xxxxxxxxxx" />
            </div>
            <ExistingPatientAlert patient={npExisting} testId="np-existing-alert" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tanggal Lahir</Label>
                <Input type="date" data-testid="np-birthday" className="mt-1 h-11" value={np.birthday} onChange={(e) => setNp({ ...np, birthday: e.target.value })} />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={np.gender} onValueChange={(v) => setNp({ ...np, gender: v })}>
                  <SelectTrigger data-testid="np-gender" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="F">Perempuan</SelectItem>
                    <SelectItem value="M">Laki-laki</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Cabang *</Label>
              <Select value={np.branch_id} onValueChange={(v) => setNp({ ...np, branch_id: v })}>
                <SelectTrigger data-testid="np-branch" className="mt-1 h-11"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent>
                  {branches?.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sumber</Label>
              <Select value={np.source} onValueChange={(v) => setNp({ ...np, source: v })}>
                <SelectTrigger data-testid="np-source" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PATIENT_SOURCES.map(([v, l]) => (<SelectItem key={v} value={v} data-testid={`np-source-opt-${v}`}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNpOpen(false)}>Batal</Button>
            <Button data-testid="np-save" onClick={saveNewPatient} disabled={npSaving}>{npSaving ? "Menyimpan..." : npExisting ? "Gunakan Pasien Ini" : "Simpan & Pilih"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
